import { AwsClient } from 'aws4fetch';
import { fetchTemporarySigV4Credentials } from '../../auth/midway';
import { getEnvironmentConfig } from '../../configurations/config';

export class FrameworkIngestApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly errorCode?: string,
  ) {
    super(message);
    this.name = 'FrameworkIngestApiError';
  }
}

export interface IngestControlInput {
  controlId: string;
  title: string;
  description?: string;
  category?: string;
  subcategory?: string;
  priority?: string;
  documentationUrl?: string;
}

export interface IngestFrameworkInput {
  name: string;
  version: string;
  ownerBindleResource: string;
  description?: string;
  status?: 'draft' | 'active' | 'deprecated' | 'archived';
  publisher?: string;
  category?: string;
  tags?: string[];
}

export interface CreateFrameworkRequest {
  framework: IngestFrameworkInput;
  controls: IngestControlInput[];
  requestedBy?: string;
}

export interface CreateFrameworkResponse {
  frameworkKey: string;
  frameworkName: string;
  version: string;
  controlCount: number;
}

export class NexusFrameworkIngestService {
  private readonly baseUrl: string;
  private readonly region: string;

  constructor() {
    const config = getEnvironmentConfig();
    this.baseUrl = config.frameworkIngestApiUrl;
    this.region = config.mappingJobsApiRegion;
  }

  isEnabled(): boolean {
    return this.baseUrl.length > 0;
  }

  async createFramework(
    req: CreateFrameworkRequest,
  ): Promise<CreateFrameworkResponse> {
    if (!this.isEnabled()) {
      throw new FrameworkIngestApiError(
        'Framework ingestion is not available in this stage',
        503,
      );
    }

    const midway = await fetchTemporarySigV4Credentials();
    if (!midway.AccessKeyId || !midway.SecretAccessKey) {
      throw new FrameworkIngestApiError(
        'Failed to retrieve Midway credentials',
        401,
      );
    }

    const aws = new AwsClient({
      accessKeyId: midway.AccessKeyId,
      secretAccessKey: midway.SecretAccessKey,
      sessionToken: midway.SessionToken,
      region: this.region,
      service: 'lambda',
    });

    const url = new URL('/v1/frameworks', this.baseUrl).toString();
    const response = await aws.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });

    if (!response.ok) {
      const payload = await response
        .json()
        .catch(() => ({ error: 'UNKNOWN', message: response.statusText }));
      throw new FrameworkIngestApiError(
        payload.message || `Framework ingest failed: ${response.status}`,
        response.status,
        payload.error,
      );
    }

    return (await response.json()) as CreateFrameworkResponse;
  }
}
